import { classNames } from '@dimjs/utils';
import { flatbizPrice, isNumber } from '@flatbiz/utils';
import { hooks } from '@wove/react';
import { FC, useMemo, useState } from 'react';
import { VirtualInput } from '../form/form-input/virtual-input';
import { useEffectCustom } from '../hooks';
import { NativeProps } from '../utils/native-props';
import './style.less';

export type StepperProps = {
  min?: number;
  max?: number;
  digits?: number;
  step?: number;
  disabled?: boolean;
  readOnly?: boolean;
  defaultValue?: string;
  value?: string;
  onChange?: (value?: string) => void;
  keyboardType?: 'number' | 'pure_number';
  hideSubtract?: boolean;
  hideAdd?: boolean;
} & NativeProps;

/**
 * 步进器、金额输入等
 * 1. value、defaultValue为字符串格式的数字，因为VirtualInput只能接收字符串
 * 2. 结合form使用最佳，结合form使用后value、onChange、defaultValue由form代理，单独设置无效
 * @param props
 * @returns
 */
export const Stepper: FC<StepperProps> = (props) => {
  const classPrefix = 'v-stepper';
  const { min, max, disabled, readOnly } = props;
  const step = props.step as number;
  const digits = props.digits as number;

  const initValue = useMemo(() => {
    return props.value !== undefined && isNumber(props.value) ? String(props.value) : undefined;
  }, [props.value]);

  const defaultValue = useMemo(() => {
    return props.defaultValue !== undefined && isNumber(props.defaultValue)
      ? String(props.defaultValue)
      : undefined;
  }, [props.defaultValue]);

  const [value, setValue] = useState(initValue !== undefined ? initValue : defaultValue);

  const getValueFormat = hooks.useCallbackRef(
    (value: string | number | undefined, type?: 'add' | 'subtract') => {
      let currentValue = value !== undefined && isNumber(value) ? Number(value) : min;
      if (!type && currentValue === undefined) {
        return undefined;
      }
      currentValue = currentValue || 0;
      let nextValue = currentValue;
      if (type) {
        nextValue =
          type === 'add' ? flatbizPrice.add(currentValue, step) : flatbizPrice.subtract(currentValue, step);
      }
      if (min !== undefined && nextValue < min) {
        nextValue = min;
      }
      if (max !== undefined && nextValue > max) {
        nextValue = max;
      }
      return digits !== 0 ? flatbizPrice.format(nextValue) : `${nextValue}`;
    },
  );

  useEffectCustom(() => {
    setValue(initValue);
  }, [initValue]);

  const onInputChange = hooks.useCallbackRef((nextValue?: string) => {
    props.onChange?.(nextValue);
  });

  const onInputBlur = hooks.useCallbackRef(() => {
    setValue(getValueFormat(value));
  });

  const addDisabled = useMemo(() => {
    if (value === undefined || max === undefined) return false;
    return Number(value) >= max;
  }, [max, value]);

  const subtractDisabled = useMemo(() => {
    if (value === undefined || min === undefined) return false;
    return Number(value) <= min;
  }, [min, value]);

  const onClickSubtract = hooks.useCallbackRef(() => {
    if (disabled || readOnly || subtractDisabled) return;
    const formatValue = getValueFormat(value, 'subtract');
    setValue(formatValue);
    onInputChange(formatValue);
  });

  const onClickAdd = hooks.useCallbackRef(() => {
    if (disabled || readOnly || addDisabled) return;
    const formatValue = getValueFormat(value, 'add');
    setValue(formatValue);
    onInputChange(formatValue);
  });

  const mainClassName = classNames(
    classPrefix,
    { [`${classPrefix}-disabled`]: disabled },
    { [`${classPrefix}-readonly`]: readOnly },
    props.className,
  );

  return (
    <div className={mainClassName} style={props.style}>
      {!props.hideSubtract && (
        <div
          className={classNames(`${classPrefix}-subtract`, {
            [`${classPrefix}-update-disbaled`]: subtractDisabled || disabled,
          })}
          onClick={onClickSubtract}
        />
      )}

      <div className={classNames(`${classPrefix}-input`)}>
        <VirtualInput
          onChange={onInputChange}
          value={`${value || ''}`}
          onBlur={onInputBlur}
          keyboardProps={{ type: props.keyboardType }}
          disabled={disabled}
          readOnly={readOnly}
        />
      </div>
      {!props.hideAdd && (
        <div
          className={classNames(`${classPrefix}-add`, {
            [`${classPrefix}-update-disbaled`]: addDisabled || disabled,
          })}
          onClick={onClickAdd}
        />
      )}
    </div>
  );
};

Stepper.defaultProps = {
  digits: 0,
  step: 1,
  keyboardType: 'pure_number',
  hideSubtract: false,
  hideAdd: false,
};
